import * as THREE from 'three';
import type { Actor } from '../sim/actor';
import { registry } from '../sim/registry';
import type { EntityId } from '../sim/types';
import type { InteractableDef, PortalDef, ZoneLayout } from '../zones/types';
import { SHARED_RIM } from './actorShading';
import type { ActorView, ActorViewOptions, Glow } from './ActorView';
import type { AssetLibrary } from './assets';
import { BlobShadows } from './Blobs';
import { type BiomeLook, lookForZone } from './Lighting';
import { GEARLESS_MODELS, MODE_WEAPON, type ToolId, toolFor } from './modelMap';
import type { PropField } from './Props';
import { renderCtx } from './RenderContext';
import { AmbientParticles, isAmbientKind } from './vfx/Ambient';
import { type Marker, buildMarker, disposeMarker, updateMarker } from './vfx/Markers';
import { PortalViews } from './vfx/Portals';
import { PropLights } from './vfx/PropLights';
import { WeaponTrail } from './vfx/Trails';
import type { VfxDirector } from './vfx/VfxDirector';

const OUTLINED_GRADES = new Set(['named', 'boss', 'raid']);
const SHIP_SEAT = new THREE.Vector3(0, 1.28 - 0.9, 0.4);

/** A creature an actor rides (mounts): model, size, where the rider sits and the rider's pose. */
export interface RideSpec {
  model: string;
  scale: number;
  seat: THREE.Vector3;
  pose: string;
}

let rideProvider: (a: Actor) => RideSpec | null = () => null;

/** A small companion that follows an actor around (pets). */
export interface CompanionSpec {
  model: string;
  scale: number;
  tint?: number;
}

let companionProvider: (a: Actor) => CompanionSpec | null = () => null;

export function setCompanionProvider(fn: (a: Actor) => CompanionSpec | null): void {
  companionProvider = fn;
}

interface Companion {
  spec: CompanionSpec;
  object: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  run: THREE.AnimationAction | null;
  idle: THREE.AnimationAction | null;
  pos: THREE.Vector3;
  yaw: number;
  speed: number;
}

/** Features decide who rides what (the mount feature reads the `mounted` status). */
export function setRideProvider(fn: (a: Actor) => RideSpec | null): void {
  rideProvider = fn;
}

interface Ride {
  spec: RideSpec;
  object: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  run: THREE.AnimationAction | null;
  idle: THREE.AnimationAction | null;
}
const FOAM = new THREE.Color(0.95, 0.98, 1.0);

/**
 * The glow an actor should show: monster states first (counter window,
 * freeze, stagger, enrage), then player identity modes — Ravager frenzy,
 * Fiendbinder form, Stormweaver Overflow / Torrent, Windfist Still Water
 * (parry) and Surge — and finally any other active identity state (Cantor's
 * full Resonance, Arsenalist's stance-swap buff) in the class hue.
 */
export function glowFor(a: Actor): { glow: Glow; hue?: number } {
  if (!a.alive) return { glow: 'none' };
  const m = a.monster;
  if (m && m.counterUntil > 0) return { glow: 'counter' };
  if (a.hasStatus('freeze')) return { glow: 'frozen' };
  if (a.hasStatus('staggered')) return { glow: 'staggered' };
  if (a.hasStatus('enraged')) return { glow: 'enraged' };
  const pl = a.player;
  if (!pl) return { glow: 'none' };
  const id = pl.identity;
  switch (id.mode) {
    case 'frenzy':
      return { glow: 'burst' };
    case 'fiend':
      return { glow: 'fiend' };
    case 'overflow':
      return { glow: 'overflow' };
    case 'torrent':
      return { glow: 'torrent' };
    default:
      break;
  }
  if (id.data.parryOpen) return { glow: 'parry' };
  if (id.data.surgeNext || id.data.empowered) return { glow: 'surge' };
  const cls = registry.classes.get(pl.classId);
  if (cls?.identity.describe?.(a).active) return { glow: 'identity', hue: cls.hue };
  return { glow: 'none' };
}

/** Weapon kind in hand: the class weapon, or the stance weapon for identity modes that swap it. */
export function weaponFor(a: Actor): string | null {
  const mode = a.player?.identity.mode;
  const stance = mode ? MODE_WEAPON[mode] : undefined;
  return stance ?? a.look.weapon ?? null;
}

export interface ZoneVisualsHost {
  readonly layout: ZoneLayout;
  readonly scene: THREE.Scene;
  readonly localPlayerId: EntityId;
  readonly interactables: InteractableDef[];
  heightAt(x: number, z: number): number;
  /** Optional gate for portals with a `requires` flag (null: those portals show sealed). */
  readonly portalOpen: ((p: PortalDef) => boolean) | null;
}

/**
 * Everything a zone draws besides terrain, water and actor bodies: batched
 * blob shadows, prop light sockets (pools, halos, pooled point lights, fire),
 * portals, interactable markers, ambient motes and the player's ship at sea.
 * It also drives the per-frame actor presentation: glows, weapons in hand,
 * broken boss parts, life-skill tools, frustum culling and animation LOD.
 */
export class ZoneVisuals {
  readonly blobs = new BlobShadows(512);
  readonly lights: PropLights;
  readonly portals: PortalViews;
  private ambient: AmbientParticles | null = null;
  private markers = new Map<string, Marker>();
  private qualityVersion = -1;
  private time = 0;
  private frustum = new THREE.Frustum();
  private projScreen = new THREE.Matrix4();
  private sphere = new THREE.Sphere();
  private focus = new THREE.Vector3();
  private disposed = false;
  readonly look: BiomeLook;
  readonly waterLevel: number | null;
  private shipT = 0;
  private auraAcc = new WeakMap<ActorView, number>();
  private trails = new Map<ActorView, WeaponTrail[]>();
  private hilt = new THREE.Vector3();
  private tip = new THREE.Vector3();
  private auraCol = new THREE.Color();
  private auraEnd = new THREE.Color();

  constructor(
    private host: ZoneVisualsHost,
    private props: PropField,
    private assets: AssetLibrary,
    private vfx: VfxDirector | null,
  ) {
    const L = host.layout;
    this.look = lookForZone(L);
    SHARED_RIM.value.setHex(this.look.charRim).multiplyScalar(this.look.charRimIntensity * 3);
    const sheet = L.water.find((w) => w.shape.kind === 'sheet');
    this.waterLevel = sheet ? sheet.level : null;
    const darkness = THREE.MathUtils.clamp(1 - (this.look.sunIntensity - 1.2) / 2, 0.15, 1);
    this.lights = new PropLights(props.sockets, { fireScale: this.look.fireScale, darkness }, vfx?.add ?? null, (x, z) => host.heightAt(x, z));
    this.portals = new PortalViews((x, z) => host.heightAt(x, z), this.waterLevel, L.props, assets, this.lights, vfx?.add ?? null, L.kind);
    host.scene.add(this.blobs.mesh, this.lights.group, this.portals.group);
    // Keeps the trail shader resident (trails come and go with every swing).
    this.trailKeeper = new WeaponTrail(new THREE.Color(1, 1, 1));
    host.scene.add(this.trailKeeper.mesh);
  }

  private trailKeeper: WeaponTrail;

  /** Ground height for actor feet: at sea everything floats on the water sheet. */
  actorGround = (x: number, z: number): number => {
    const h = this.host.heightAt(x, z);
    return this.host.layout.kind === 'sea' && this.waterLevel !== null ? Math.max(h, this.waterLevel) : h;
  };

  // ------------------------------------------------------------------ markers

  /** Builds (or rebuilds) an interactable's marker; returns its root object. */
  addMarker(it: InteractableDef): THREE.Object3D {
    this.removeMarker(it.id);
    const y = this.markerGround(it);
    const mk = buildMarker(it, this.assets, y, () => this.disposed || !this.markers.has(it.id));
    this.markers.set(it.id, mk);
    this.host.scene.add(mk.group);
    if (mk.light) this.lights.setDynamic(`marker:${it.id}`, mk.light);
    return mk.group;
  }

  /** Fishing spots float on the water; everything else sits on the terrain. */
  private markerGround(it: InteractableDef): number {
    const h = this.host.heightAt(it.x, it.z);
    return it.kind === 'fish' && this.waterLevel !== null ? Math.max(h, this.waterLevel) : h;
  }

  removeMarker(id: string): void {
    const mk = this.markers.get(id);
    if (!mk) return;
    this.host.scene.remove(mk.group);
    disposeMarker(mk);
    this.markers.delete(id);
    this.lights.setDynamic(`marker:${id}`, null);
  }

  // ------------------------------------------------------------------ views

  /** Construction options for an actor's view. */
  viewOptions(a: Actor): ActorViewOptions {
    return { outline: renderCtx.profile.outlines && !!a.monster && OUTLINED_GRADES.has(a.monster.grade) };
  }

  /** One-time setup of a freshly built view (weapon, ship, shadows). */
  decorate(v: ActorView, a: Actor): void {
    if (a.kind === 'player' && !GEARLESS_MODELS.has(v.assetId)) v.setWeapon(weaponFor(a));
    v.setCastShadow(this.castsShadow(v, a));
    if (a.kind === 'player' && this.host.layout.kind === 'sea') {
      void this.assets.load('ship_player').then((t) => {
        if (this.disposed || t.placeholder) return;
        const ship = this.assets.instantiate(t);
        ship.position.y = -0.9;
        ship.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) m.castShadow = true;
        });
        v.setMount(ship, SHIP_SEAT);
      });
    }
  }

  private rides = new Map<ActorView, Ride>();
  private pendingRides = new Set<ActorView>();

  /** Mounts: builds, animates and removes the ridden creature to match the actor's state. */
  private syncRide(dt: number, a: Actor, v: ActorView): void {
    const spec = a.alive ? rideProvider(a) : null;
    const ride = this.rides.get(v);
    if (ride && (!spec || spec.model !== ride.spec.model)) {
      v.setMount(null);
      v.ridePose = null;
      ride.mixer.stopAllAction();
      this.rides.delete(v);
    }
    if (spec && !this.rides.has(v) && !this.pendingRides.has(v)) {
      this.pendingRides.add(v);
      void this.assets.load(spec.model).then((t) => {
        this.pendingRides.delete(v);
        if (this.disposed || t.placeholder || rideProvider(a)?.model !== spec.model) return;
        const object = this.assets.instantiate(t);
        object.scale.setScalar(spec.scale);
        object.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) m.castShadow = true;
        });
        const mixer = new THREE.AnimationMixer(object);
        const clip = (name: string) => t.clips.find((c) => c.name === name) ?? null;
        const run = clip('Run') ? mixer.clipAction(clip('Run') as THREE.AnimationClip) : null;
        const idle = clip('Idle') ? mixer.clipAction(clip('Idle') as THREE.AnimationClip) : null;
        idle?.play();
        v.setMount(object, spec.seat);
        v.ridePose = spec.pose;
        this.rides.set(v, { spec, object, mixer, run, idle });
      });
      return;
    }
    if (!ride || !spec) return;
    // The creature runs while the rider moves.
    const moving = a.speed > 0.4;
    const want = moving ? ride.run : ride.idle;
    const other = moving ? ride.idle : ride.run;
    if (want && !want.isRunning()) want.reset().fadeIn(0.15).play();
    if (other?.isRunning()) other.fadeOut(0.15);
    if (ride.run) ride.run.timeScale = Math.min(1.8, Math.max(0.7, a.speed / 7));
    ride.mixer.update(dt);
  }

  private companions = new Map<EntityId, Companion>();
  private pendingCompanions = new Set<EntityId>();

  /** Pets trot after their owner, keeping a short distance behind. */
  private syncCompanion(dt: number, a: Actor, v: ActorView): void {
    const spec = a.alive ? companionProvider(a) : null;
    const c = this.companions.get(a.id);
    if (c && (!spec || spec.model !== c.spec.model)) {
      this.host.scene.remove(c.object);
      c.mixer.stopAllAction();
      this.companions.delete(a.id);
    }
    if (spec && !this.companions.has(a.id) && !this.pendingCompanions.has(a.id)) {
      this.pendingCompanions.add(a.id);
      void this.assets.load(spec.model).then((t) => {
        this.pendingCompanions.delete(a.id);
        if (this.disposed || t.placeholder || companionProvider(a)?.model !== spec.model) return;
        const object = this.assets.instantiate(t);
        object.scale.setScalar(spec.scale);
        if (spec.tint !== undefined) {
          const tint = new THREE.Color(spec.tint);
          object.traverse((o) => {
            const m = o as THREE.Mesh;
            if (!m.isMesh) return;
            m.material = (m.material as THREE.MeshStandardMaterial).clone();
            (m.material as THREE.MeshStandardMaterial).color?.lerp(tint, 0.55);
          });
        }
        const mixer = new THREE.AnimationMixer(object);
        const clip = (name: string) => t.clips.find((cl) => cl.name === name) ?? null;
        const run = clip('Run') ? mixer.clipAction(clip('Run') as THREE.AnimationClip) : null;
        const idle = clip('Idle') ? mixer.clipAction(clip('Idle') as THREE.AnimationClip) : null;
        idle?.play();
        const pos = v.root.position.clone().add(new THREE.Vector3(1, 0, -1));
        object.position.copy(pos);
        this.host.scene.add(object);
        this.companions.set(a.id, { spec, object, mixer, run, idle, pos, yaw: 0, speed: 0 });
      });
      return;
    }
    if (!c || !spec) return;
    // Aim for a spot behind and beside the owner; jog there, then settle.
    const back = new THREE.Vector3(Math.sin(a.facing + 2.4) * 1.6, 0, Math.cos(a.facing + 2.4) * 1.6);
    const goal = v.root.position.clone().add(back);
    const dx = goal.x - c.pos.x;
    const dz = goal.z - c.pos.z;
    const d = Math.hypot(dx, dz);
    if (d > 25) c.pos.copy(goal); // teleports, zone edges
    const want = d > 0.4 ? Math.min(d * 3, Math.max(4, a.speed * 1.15)) : 0;
    c.speed += (want - c.speed) * Math.min(1, dt * 8);
    if (d > 0.01) {
      const step = Math.min(d, c.speed * dt);
      c.pos.x += (dx / d) * step;
      c.pos.z += (dz / d) * step;
      if (c.speed > 0.3) c.yaw = Math.atan2(dx, dz);
    }
    c.pos.y = this.host.heightAt(c.pos.x, c.pos.z);
    c.object.position.copy(c.pos);
    c.object.rotation.y += (Math.atan2(Math.sin(c.yaw - c.object.rotation.y), Math.cos(c.yaw - c.object.rotation.y))) * Math.min(1, dt * 10);
    const moving = c.speed > 0.5;
    const on = moving ? c.run : c.idle;
    const off = moving ? c.idle : c.run;
    if (on && !on.isRunning()) on.reset().fadeIn(0.15).play();
    if (off?.isRunning()) off.fadeOut(0.15);
    if (c.run) c.run.timeScale = Math.min(2, Math.max(0.8, c.speed / 4));
    c.mixer.update(dt);
  }

  private castsShadow(v: ActorView, a: Actor): boolean {
    const q = renderCtx.profile;
    if (!q.shadows || q.creatureShadows === 'none') return false;
    if (q.creatureShadows === 'all' || a.kind === 'player') return true;
    return v.blobRadius > 1.1 || (!!a.monster && a.monster.grade !== 'normal' && a.monster.grade !== 'minion');
  }

  /**
   * Per-frame presentation of every actor view plus the zone effects. Views
   * outside the camera frustum are hidden and skip skeleton updates; far ones
   * animate at 15 Hz.
   */
  frame(dt: number, alpha: number, actors: Iterable<Actor>, views: Map<EntityId, ActorView>, time: number): void {
    this.time += dt;
    const q = renderCtx.profile;
    const qualityChanged = this.qualityVersion !== renderCtx.version;
    if (qualityChanged) {
      this.qualityVersion = renderCtx.version;
      this.props.applyQuality(q);
      this.syncAmbient(q.ambientParticles);
      if (this.vfx) this.vfx.density = q.particleDensity;
    }
    const cam = renderCtx.camera;
    if (cam) {
      this.projScreen.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
      this.frustum.setFromProjectionMatrix(this.projScreen);
    }
    const me = views.get(this.host.localPlayerId);
    if (me) this.focus.copy(me.root.position);
    else if (cam) this.focus.set(cam.position.x, 0, cam.position.z - 12);

    this.blobs.begin();
    for (const a of actors) {
      const v = views.get(a.id);
      if (!v) continue;
      if (qualityChanged) v.setCastShadow(this.castsShadow(v, a));
      const g = glowFor(a);
      v.glow = g.glow;
      if (g.hue !== undefined) v.glowColor.setHex(g.hue);
      v.counterLeft = a.monster?.counterUntil ?? 0;
      if (a.kind === 'player' && !GEARLESS_MODELS.has(v.assetId)) v.setWeapon(weaponFor(a));
      const parts = a.monster?.parts;
      if (parts) for (const p of parts) if (p.broken && !v.isPartBroken(p.id)) v.setPartBroken(p.id);
      if (a.id === this.host.localPlayerId) v.toolHint = this.nearTool(a);
      if (this.host.layout.kind !== 'sea') {
        this.syncRide(dt, a, v);
        this.syncCompanion(dt, a, v);
      }
      // Culling (last frame's camera; generous sphere so shadows and big swings never pop).
      this.sphere.center.set(a.pos.x, v.root.position.y + v.headHeight * 0.5, a.pos.z);
      this.sphere.radius = v.headHeight * 0.8 + 3;
      const inView = !cam || this.frustum.intersectsSphere(this.sphere);
      const far = Math.hypot(a.pos.x - this.focus.x, a.pos.z - this.focus.z) > q.animNear;
      v.root.visible = inView;
      v.update(dt, a, alpha, this.actorGround, inView, far ? 1 / 15 : 0);
      // Corpses sink into the ground after their death animation.
      if (!a.alive && a.kind === 'monster') {
        const t = time - a.diedAt;
        if (t > 2.5) v.root.position.y -= Math.min(2, (t - 2.5) * 0.8);
      }
      if (inView && (!v.mounted || this.rides.has(v))) {
        const gp = v.ground;
        this.blobs.push(gp.x, gp.y, gp.z, v.blobRadius, v.blobAlpha);
      }
      if (v.mounted && !this.rides.has(v) && this.vfx) this.wake(dt, a, v);
      if (inView && this.vfx) this.emitAura(dt, v, q.particleDensity);
      if (a.kind === 'player') this.updateTrails(dt, a, v, inView);
    }
    // Companions of actors that left.
    for (const [id, c] of this.companions) {
      if (views.has(id)) continue;
      this.host.scene.remove(c.object);
      c.mixer.stopAllAction();
      this.companions.delete(id);
    }
    // Drop trails of views that are gone.
    for (const [v, list] of this.trails) {
      if (views.get(v.id) === v) continue;
      for (const t of list) {
        this.host.scene.remove(t.mesh);
        t.dispose();
      }
      this.trails.delete(v);
    }
    this.blobs.end();

    for (const mk of this.markers.values()) updateMarker(mk, this.time, dt);
    this.portals.sync(this.host.layout.portals, (p) => !p.requires || (this.host.portalOpen?.(p) ?? false));
    this.portals.update(dt, this.focus, q.particleDensity);
    this.lights.update(dt, this.focus, q.pointLights, q.particleDensity);
    this.ambient?.update(dt, this.focus);
    this.props.update(dt);
  }

  /** Tool for the local player's Gather/Fish clip, from the nearest node. */
  private nearTool(a: Actor): ToolId | null {
    let best: InteractableDef | null = null;
    let bestD = 4;
    for (const it of this.host.interactables) {
      const d = Math.hypot(it.x - a.pos.x, it.z - a.pos.z);
      if (d < bestD) {
        bestD = d;
        best = it;
      }
    }
    return best ? toolFor(best.kind, best.data?.node) : null;
  }

  /** Melee weapon trails in the class hue while a swing clip plays. */
  private updateTrails(dt: number, a: Actor, v: ActorView, inView: boolean): void {
    let list = this.trails.get(v);
    const spec = v.trailSockets();
    const swinging = inView && v.swinging && spec !== null;
    if (!list) {
      if (!swinging || !spec) return;
      const hue = new THREE.Color(registry.classes.get(a.player?.classId ?? '')?.hue ?? 0xffe0a0);
      list = spec.bones.map(() => new WeaponTrail(hue));
      for (const t of list) this.host.scene.add(t.mesh);
      this.trails.set(v, list);
    }
    let alive = false;
    list.forEach((t, i) => {
      const bone = spec?.bones[i];
      if (swinging && bone && spec) v.sampleBlade(bone, spec.length, this.hilt, this.tip);
      if (t.update(dt, swinging && !!bone, this.hilt, this.tip)) alive = true;
    });
    if (!alive && !swinging) {
      for (const t of list) {
        this.host.scene.remove(t.mesh);
        t.dispose();
      }
      this.trails.delete(v);
    }
  }

  /** Identity-mode auras: motes rising around the body in the glow colour. */
  private emitAura(dt: number, v: ActorView, density: number): void {
    const { rate, color } = v.aura;
    if (rate <= 0 || !this.vfx) return;
    let acc = (this.auraAcc.get(v) ?? 0) + dt * rate * density;
    const n = Math.floor(acc);
    acc -= n;
    this.auraAcc.set(v, acc);
    if (n <= 0) return;
    const p = v.root.position;
    const r = 0.45 * v.scale;
    const hgt = v.headHeight - 0.35;
    this.auraCol.copy(color).multiplyScalar(2.4);
    this.auraEnd.copy(color).multiplyScalar(0.25);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      this.vfx.add.spawn({
        x: p.x + Math.cos(a) * r,
        y: p.y + Math.random() * hgt * 0.8,
        z: p.z + Math.sin(a) * r,
        vx: Math.cos(a) * 0.25,
        vy: 1.1 + Math.random() * 1.2,
        vz: Math.sin(a) * 0.25,
        color: this.auraCol,
        colorEnd: this.auraEnd,
        size: 0.13 + Math.random() * 0.08,
        sizeEnd: 0.02,
        life: 0.55 + Math.random() * 0.35,
        drag: 0.8,
      });
    }
  }

  /** Foam trail behind the ship while sailing. */
  private wake(dt: number, a: Actor, v: ActorView): void {
    const soft = this.vfx?.soft;
    if (!soft || a.speed < 0.8) return;
    this.shipT -= dt;
    if (this.shipT > 0) return;
    this.shipT = 0.07;
    const back = { x: -Math.sin(a.facing), z: -Math.cos(a.facing) };
    const side = { x: Math.cos(a.facing), z: -Math.sin(a.facing) };
    const y = (this.waterLevel ?? v.root.position.y) + 0.1;
    for (const s of [-1, 1]) {
      soft.spawn({
        x: a.pos.x + back.x * 5.2 + side.x * s * 1.2,
        y,
        z: a.pos.z + back.z * 5.2 + side.z * s * 1.2,
        vx: side.x * s * 0.9 + back.x * 0.4,
        vy: 0.05,
        vz: side.z * s * 0.9 + back.z * 0.4,
        color: FOAM,
        size: 0.7,
        sizeEnd: 2.2,
        life: 1.3,
        drag: 1.2,
        alpha: 0.28,
      });
    }
  }

  private syncAmbient(budget: number): void {
    const kind = this.host.layout.ambient;
    if (this.ambient) {
      this.host.scene.remove(this.ambient.mesh);
      this.ambient.dispose();
      this.ambient = null;
    }
    if (budget <= 0 || !isAmbientKind(kind)) return;
    this.ambient = new AmbientParticles(kind, budget);
    this.host.scene.add(this.ambient.mesh);
  }

  dispose(): void {
    this.disposed = true;
    for (const list of this.trails.values()) for (const t of list) t.dispose();
    this.trails.clear();
    for (const id of [...this.markers.keys()]) this.removeMarker(id);
    this.portals.dispose();
    this.lights.dispose();
    this.blobs.dispose();
    this.ambient?.dispose();
    this.host.scene.remove(this.blobs.mesh, this.lights.group, this.portals.group, this.trailKeeper.mesh);
    this.trailKeeper.dispose();
  }
}
